import { VariableType } from 'json-to-graphql-query';

import { Members, MembersFunctionTranslation } from '@/enums/members.enum';
import { Team } from '@/interfaces/team';
import { EntityResource } from './entity.resource';
import { TeamFilterVariables } from './queryVariables/filters/team.filters';
import { ProfileArgs, ProfileVariables } from './queryVariables/profile';

/**
 * Resource grup treningowych
 *
 * @export
 * @class TeamResource
 * @extends {EntityResource<Team>}
 */
export class TeamResource extends EntityResource<Team> {
  public getFields = {
    id: true,
    name: true,
    sport: {
      id: true,
      name: true
    },
    club: {
      id: true,
      name: true
    }
  };

  public listFields = {
    id: true,
    name: true
  };

  protected listVariables = TeamFilterVariables;

  constructor() {
    super({
      entityName: 'team'
    });
  }

  public playerInvite(email: string, teamId: number) {
    return this.inviteMember(email, teamId, MembersFunctionTranslation[Members.PLAYER]);
  }
  public coachInvite(email: string, teamId: number) {
    return this.inviteMember(email, teamId, MembersFunctionTranslation[Members.COACH]);
  }

  /**
   * Zaprasza członka do grupy treningowej
   *
   * @param {string} email
   * @param {number} teamId
   * @param {string} memberFunction
   * @returns {Promise<string>}
   * @memberof TeamResource
   */
  private inviteMember(email: string, teamId: number, memberFunction: string): Promise<string> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { email: 'String!', teamId: 'Int!' },
        [memberFunction]: {
          __args: {
            email: new VariableType('email'),
            teamId: new VariableType('teamId')
          }
        }
      }
    });
    return this.mutation({ mutation, variables: { email, teamId } }, memberFunction);
  }

  /**
   * Dodaje zawodnika do grupy treningowej
   *
   * @param {*} profile
   * @param {number} teamId
   * @returns {Promise<number>}
   * @memberof TeamResource
   */
  public playerAdd(profile: any, teamId: number): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { ...ProfileVariables, teamId: 'Int!' },
        playerAdd: {
          __args: {
            ...ProfileArgs,
            teamId: new VariableType('teamId')
          }
        }
      }
    });
    return this.mutation({ mutation, variables: { profile, teamId } }, 'playerAdd');
  }

  /**
   * Rejestruje konto zawodnika grupy treningowej
   *
   * @param {string} email
   * @param {number} profileId
   * @param {number} teamId
   * @returns {Promise<number>}
   * @memberof TeamResource
   */
  public playerAccountRegister(email: string, profileId: number, teamId: number): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { email: 'String!', profileId: 'Int!', teamId: 'Int!' },
        playerAccountRegister: {
          __args: {
            email: new VariableType('email'),
            profileId: new VariableType('profileId'),
            teamId: new VariableType('teamId')
          }
        }
      }
    });
    return this.mutation({ mutation, variables: { email, profileId, teamId } }, 'playerAccountRegister');
  }

  /**
   * Pobiera grupę treningową na podstawie tokenu
   *
   * @param {string} token
   * @returns {Promise<Team>}
   * @memberof TeamResource
   */
  public getByToken(token: string): Promise<Team> {
    const query = this.prepareOptions({
      query: {
        __variables: { token: 'String!' },
        teamByToken: {
          __args: { token: new VariableType('token') },
          name: true,
          club: {
            name: true
          }
        }
      }
    });
    return this.query({ query, variables: { token } }, 'teamByToken');
  }
}

export const teamResource = new TeamResource();
